import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HistoryIcon from '@mui/icons-material/History'
import { purchaseOrderImportService } from '../services'
import ImportSessionStatusChip from '../components/purchase-order-import/ImportSessionStatusChip'
import { formatDateTime } from '../utils/dateFormat'

const PurchaseOrderCsvUploadPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orgId = searchParams.get('orgId')
  const orgName = searchParams.get('orgName') || `Shop ${orgId}`

  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [sessions, setSessions] = useState([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [isDragOver, setIsDragOver] = useState(false)
  const pollRef = useRef(null)

  const fetchSessions = useCallback(async () => {
    if (!orgId) return
    setSessionsLoading(true)
    try {
      const data = await purchaseOrderImportService.listSessions({
        orgId: Number(orgId),
        page: page + 1,
        limit: rowsPerPage,
      })
      setSessions(data.items || [])
      setTotalCount(data.pagination?.total || 0)
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to load import history',
      })
    } finally {
      setSessionsLoading(false)
    }
  }, [orgId, page, rowsPerPage])

  useEffect(() => {
    if (activeTab === 1) {
      fetchSessions()
    }
  }, [activeTab, fetchSessions])

  useEffect(() => {
    if (activeTab !== 1) return undefined
    pollRef.current = setInterval(fetchSessions, 5000)
    return () => clearInterval(pollRef.current)
  }, [activeTab, fetchSessions])

  const handleDownloadSample = () => {
    const csv =
      'article_code,quantity,selling_price,mrp\nABC123,10,98.11,110.00\nXYZ789,5,,\n'
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'purchase-order-import-sample.csv'
    link.click()
    URL.revokeObjectURL(link.href)
  }

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a CSV file' })
      return
    }
    if (!orgId) {
      setMessage({ type: 'error', text: 'Shop Id is required' })
      return
    }
    setLoading(true)
    setMessage(null)
    try {
      const result = await purchaseOrderImportService.uploadCsv({
        file,
        orgId: Number(orgId),
      })
      if (result.sessionId) {
        navigate(`/orders/purchase/create/upload/${result.sessionId}`)
      } else {
        setMessage({ type: 'success', text: result.message || 'Upload started' })
      }
    } catch (err) {
      const msg = err.response?.data?.message
      setMessage({
        type: 'error',
        text: Array.isArray(msg) ? msg.join(', ') : msg || 'Upload failed',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    const dropped = e.dataTransfer.files?.[0]
    if (dropped?.name?.endsWith('.csv')) {
      setFile(dropped)
    } else {
      setMessage({ type: 'error', text: 'Please upload a CSV file only' })
    }
  }

  if (!orgId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          Select a shop on the create purchase order page first.
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/orders/purchase/create')}>
          Back to create order
        </Button>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/orders/purchase/create')}
        sx={{ mb: 2, color: '#666' }}
      >
        Back to create order
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Upload purchase order CSV
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        CSV import for <strong>{orgName}</strong> — order will be created for the
        org&apos;s primary app user in CREATED status.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<CloudUploadIcon />} iconPosition="start" label="Upload" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Upload history" />
      </Tabs>

      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: isDragOver ? 'primary.main' : '#ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: isDragOver ? 'action.hover' : 'transparent',
                mb: 2,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                Drag and drop your CSV file here
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Required columns: article_code (or sku), quantity. Optional:
                selling_price, mrp.
              </Typography>
              <Button variant="outlined" component="label">
                Choose file
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button startIcon={<DownloadIcon />} onClick={handleDownloadSample}>
                Download sample CSV
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                onClick={handleUpload}
                disabled={loading || !file}
                sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c00000' } }}
              >
                Upload and process
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Divider sx={{ mb: 2 }} />
            {sessionsLoading && !sessions.length ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>File</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Resolved</TableCell>
                        <TableCell align="right">Failed</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sessions.map((s) => (
                        <TableRow key={s.id} hover>
                          <TableCell>{s.file_name || '—'}</TableCell>
                          <TableCell>
                            {formatDateTime(s.created_at)}
                          </TableCell>
                          <TableCell align="right">{s.total_rows}</TableCell>
                          <TableCell align="right">{s.resolved_rows}</TableCell>
                          <TableCell align="right">{s.failed_rows}</TableCell>
                          <TableCell>
                            <ImportSessionStatusChip status={s.status} />
                          </TableCell>
                          <TableCell>
                            {s.status === 'ready_for_review' && (
                              <Button
                                size="small"
                                onClick={() =>
                                  navigate(
                                    `/orders/purchase/create/upload/${s.id}/review`,
                                  )
                                }
                              >
                                Review
                              </Button>
                            )}
                            {['queued', 'parsing', 'resolving'].includes(s.status) && (
                              <Button
                                size="small"
                                onClick={() =>
                                  navigate(`/orders/purchase/create/upload/${s.id}`)
                                }
                              >
                                View progress
                              </Button>
                            )}
                            {s.status === 'submitted' && s.created_order_id && (
                              <Button
                                size="small"
                                onClick={() =>
                                  navigate(`/orders/${s.created_order_id}`, {
                                    state: { ordersListPath: '/orders/purchase' },
                                  })
                                }
                              >
                                View order
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!sessions.length && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No import sessions yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10))
                    setPage(0)
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  )
}

export default PurchaseOrderCsvUploadPage
